/** Anfragenübersicht (F19/F20): eigene Meldungen, für Kundenadmin/Reviewer
 * zusätzlich die Organisations-Sicht. Karten-Liste nach Design-Handoff (Screen 02). */

import { useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Building2,
  CalendarDays,
  ChevronRight,
  MessageCircle,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { api, apiError } from "@/services/api"
import {
  datumKurz,
  datumZeit,
  PRIO_BADGE,
  PRIO_LABEL,
  STATUS_LABEL,
  STATUS_PUNKT,
  TYP_LABEL,
  ZUG_LABEL,
} from "@/lib/labels"
import { StatusKette } from "@/components/meldung/StatusKette"
import { Zeitstrahl } from "@/components/meldung/Zeitstrahl"
import { useKunden } from "@/lib/kunden"
import { reihenfolgeMerken } from "@/lib/meldungsReihenfolge"
import { useAuthStore } from "@/store/auth"
import type { Report, ReportStatus } from "@/types"

/** „Offene" Meldungen = aktive Pipeline (ohne Entwürfe und Abschluss-Stati). */
const OFFENE_STATI: ReportStatus[] = ["neu", "gesichtet", "in_umsetzung"]

/** Chip-Reihenfolge (logische Pipeline). „entwurf" nur in der eigenen Sicht. */
const STATUS_REIHENFOLGE: ReportStatus[] = [
  "entwurf",
  "neu",
  "gesichtet",
  "in_umsetzung",
  "erledigt",
  "abgelehnt",
  "verworfen",
]


type Sicht = "alle" | "meine" | "andere"
type Zug = "dev" | "melder" | null

/** Hans 06.10.2026: Filter bleiben je Benutzer erhalten, auch nach dem
 * Verlassen der Seite. Gespeichert im Browser (localStorage), getrennt je
 * Benutzer-ID — ein zweiter Benutzer am selben Rechner erbt nichts. */
interface GemerkteFilter {
  sicht: Sicht
  status: ReportStatus[]
  zug: Zug
  datumsstrahl: boolean
  tag: string | null
}

const filterSchluessel = (sub: string) => `testerwerk:uebersicht-filter:${sub}`
function filterLaden(sub: string): Partial<GemerkteFilter> {
  if (!sub) return {}
  try {
    const roh = JSON.parse(localStorage.getItem(filterSchluessel(sub)) ?? "null")
    if (!roh || typeof roh !== "object") return {}
    const f: Partial<GemerkteFilter> = {}
    if (["alle", "meine", "andere"].includes(roh.sicht)) f.sicht = roh.sicht
    if (Array.isArray(roh.status)) {
      f.status = roh.status.filter((s: unknown): s is ReportStatus =>
        STATUS_REIHENFOLGE.includes(s as ReportStatus)
      )
    }
    if (roh.zug === "dev" || roh.zug === "melder" || roh.zug === null) f.zug = roh.zug
    if (typeof roh.datumsstrahl === "boolean") f.datumsstrahl = roh.datumsstrahl
    if (typeof roh.tag === "string" && /^\d{4}-\d{2}-\d{2}$/.test(roh.tag)) f.tag = roh.tag
    return f
  } catch {
    return {}
  }
}

function filterSpeichern(sub: string, f: GemerkteFilter): void {
  if (!sub) return
  try {
    localStorage.setItem(filterSchluessel(sub), JSON.stringify(f))
  } catch {
    // Speicher voll oder gesperrt (privates Fenster) — dann eben nur für diese Sitzung
  }
}

/** Pill-Beschriftung wie die Karte (Hans 29.09.): die Issue-Nummer, wenn es
 * eine gibt — nicht die interne Meldungs-ID, die sich mit ihr verwechseln lässt. */
function pillText(r: Report): string {
  const titel = r.titel || "ohne Titel"
  return r.forgejoIssueNr != null ? `#${r.forgejoIssueNr} · ${titel}` : titel
}

/** Laufende Uhr hh:mm:ss neben der Überschrift (Hans 06.10.2026) */
function Uhr() {
  const [jetzt, setJetzt] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setJetzt(new Date()), 1000)
    return () => window.clearInterval(t)
  }, [])
  return (
    <time
      dateTime={jetzt.toISOString()}
      className="text-[26px] font-bold tracking-tight tabular-nums text-muted-foreground"
    >
      {jetzt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </time>
  )
}

function MeldungsKarte({ report, orgSicht, appName, eigene, issueVerlinken }: {
  report: Report
  orgSicht: boolean
  appName: string | null
  /** Meldung des angemeldeten Benutzers — senkrechter Farbbalken links. */
  eigene: boolean
  /** Issue-Nummer als Link zu Forgejo — nur Reviewer (Hans 06.10.2026) */
  issueVerlinken: boolean
}) {
  const istEntwurf = report.status === "entwurf"
  const queryClient = useQueryClient()
  const loeschen = useMutation({
    mutationFn: () => api.reports.remove(report.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success("Entwurf gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const meta = istEntwurf
    ? `noch nicht abgesendet · zuletzt bearbeitet ${datumKurz(report.createdAt)}`
    : [
        report.typ ? TYP_LABEL[report.typ] : null,
        orgSicht && report.melderName ? `gemeldet von ${report.melderName}` : null,
        // Hans 06.10.2026: Erstellung mit Uhrzeit, dazu der letzte Kontakt
        datumZeit(report.createdAt),
        report.letzterKontaktAm ? `letzter Kontakt ${datumZeit(report.letzterKontaktAm)}` : null,
        appName,
      ]
        .filter(Boolean)
        .join(" · ")

  return (
    <Link
      to={`/meldungen/${report.id}`}
      className={`block rounded-xl px-5 py-4 transition-colors ${
        istEntwurf
          ? "border border-dashed bg-card/60 hover:bg-card"
          : "border bg-card shadow-card hover:border-primary/30"
      } ${eigene ? "border-l-[3px] border-l-primary" : ""}`}
    >
      <div className="flex items-center gap-4">
        <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_PUNKT[report.status]}`} />
        <div className="min-w-0 flex-1">
          <div className={`truncate text-[15px] font-semibold ${istEntwurf ? "text-muted-foreground" : ""}`}>
            {report.forgejoIssueNr != null && !issueVerlinken && (
              <span className="mr-1.5 font-semibold text-primary">#{report.forgejoIssueNr}</span>
            )}
            {report.forgejoIssueNr != null && issueVerlinken && (
              // Karte ist selbst ein <Link> — daher kein verschachteltes <a>,
              // sondern Klick-Handler, der die Karten-Navigation unterdrückt
              <span
                role="link"
                title="Forgejo-Issue öffnen"
                className={`mr-1.5 font-semibold text-primary ${report.forgejoIssueUrl ? "hover:underline" : ""}`}
                onClick={(e) => {
                  if (!report.forgejoIssueUrl) return
                  e.preventDefault()
                  e.stopPropagation()
                  window.open(report.forgejoIssueUrl, "_blank", "noopener")
                }}
              >
                #{report.forgejoIssueNr}
              </span>
            )}
            {report.titel || "ohne Titel"}
          </div>
          <div className="mt-0.5 truncate text-[13px] text-muted-foreground">{meta}</div>
        </div>
        {/* Unbeantwortete Rückfrage der Entwicklung — der Melder ist am Zug */}
        {report.rueckfrageOffen && !istEntwurf && (
          <span
            title="Die Entwicklung hat eine Rückfrage gestellt, die noch unbeantwortet ist"
            className="flex shrink-0 items-center gap-1 rounded-full bg-warning px-2.5 py-1 text-xs font-bold text-warning-foreground"
          >
            <MessageCircle className="h-3 w-3" />
            Rückfrage offen
          </span>
        )}
        {/* Scheibe 2 (#43): Ungelesen-Badge aus dem serverseitigen Lesestand —
            stimmt damit auf jedem Gerät, nicht nur im zuletzt benutzten Browser */}
        {report.ungeleseneKommentare > 0 && (
          <span
            title="Neue Chat-Nachrichten"
            className="flex shrink-0 items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
          >
            <MessageCircle className="h-3 w-3" />
            {report.ungeleseneKommentare}
          </span>
        )}
        {!istEntwurf && (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${PRIO_BADGE[report.prioritaet]}`}
          >
            {PRIO_LABEL[report.prioritaet]}
          </span>
        )}
        {istEntwurf && (
          <button
            type="button"
            title="Entwurf löschen"
            disabled={loeschen.isPending}
            onClick={(e) => {
              // Karte ist ein <Link> — Navigation unterdrücken
              e.preventDefault()
              e.stopPropagation()
              if (window.confirm("Diesen Entwurf endgültig löschen?")) loeschen.mutate()
            }}
            className="shrink-0 rounded-lg p-1.5 text-muted-foreground/70 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
      </div>
      {/* testerwerk#122: der Weg der Meldung als eigene Zeile über die ganze
          Kartenbreite — so stehen die letzten Schritte aller Karten genau
          untereinander, egal welche Abzeichen oben rechts stehen. */}
      <div className="mt-2.5 pl-6 pr-8">
        <StatusKette report={report} kompakt />
      </div>
    </Link>
  )
}

export function UebersichtPage() {
  // Perspektive der Pill-Reihen (12.08.2026): die blaue Reihe ist der
  // Eingangskorb der ENTWICKLUNG — Kundenadmins sind Kunden, nicht Entwicklung.
  // Seit Scheibe 6 (#43) laden ohnehin alle Nutzer die Instanz-Sicht.
  const istReviewer = useAuthStore((s) => s.isReviewer())
  const meinSub = useAuthStore((s) => s.user?.id ?? "")
  // Scheibe 6 (#43, 12.08.2026): Meldungen gehören der INSTANZ — jeder
  // Nutzer lädt alles (plus eigene Entwürfe); „Meine/Andere" ist nur noch
  // ein Filter auf der geladenen Liste.
  const scope = "alle" as const
  // Gemerkte Filter des Benutzers einmalig beim Laden der Seite übernehmen.
  // Ist der Benutzer beim ersten Zeichnen noch nicht bekannt, holt der
  // Effekt unten das nach — und vorher wird NICHTS gespeichert, sonst
  // überschreiben die Standardwerte die gemerkte Auswahl (Hans 06.10.2026).
  const [gemerkt] = useState(() => filterLaden(meinSub))
  const filterGeladenFuer = useRef<string>(meinSub)
  const geradeGeladen = useRef(false)
  const [sicht, setSicht] = useState<Sicht>(gemerkt.sicht ?? "alle")
  const [statusFilter, setStatusFilter] = useState<ReportStatus[]>(gemerkt.status ?? OFFENE_STATI)
  // Wer ist dran (#101): zusätzlicher Filter, kombinierbar mit dem Status-Filter
  const [zugFilter, setZugFilter] = useState<Zug>(gemerkt.zug ?? null)

  // Klick vs. Doppelklick unterscheiden: der Einzelklick wird kurz verzögert;
  // ein eintreffender Doppelklick storniert ihn (isolieren statt umschalten).
  const klickTimer = useRef<number | null>(null)
  const umschalten = (s: ReportStatus) =>
    setStatusFilter((akt) => (akt.includes(s) ? akt.filter((x) => x !== s) : [...akt, s]))
  const chipKlick = (s: ReportStatus) => {
    if (klickTimer.current != null) {
      window.clearTimeout(klickTimer.current)
      klickTimer.current = null
      return
    }
    klickTimer.current = window.setTimeout(() => {
      klickTimer.current = null
      umschalten(s)
    }, 220)
  }
  const chipDoppelklick = (s: ReportStatus) => {
    if (klickTimer.current != null) {
      window.clearTimeout(klickTimer.current)
      klickTimer.current = null
    }
    setStatusFilter([s]) // isolieren: alle anderen abwählen
  }

  // Entwürfe sind in beiden Scopes dabei (in „alle" nur die eigenen, F20).
  const sichtbareStati = STATUS_REIHENFOLGE
  const istDefault =
    statusFilter.length === OFFENE_STATI.length &&
    OFFENE_STATI.every((s) => statusFilter.includes(s))

  // Datumsstrahl (Hans 06.10.2026): Meldungen als Balken auf der Zeitachse
  // statt Kartenliste — es gelten dieselben Filter.
  const [datumsstrahlAn, setDatumsstrahlAn] = useState(gemerkt.datumsstrahl ?? false)
  // Gewählter Tag im Zeitstrahl (YYYY-MM-DD) — nur Meldungen mit Aktivität
  const [strahlTag, setStrahlTag] = useState<string | null>(gemerkt.tag ?? null)

  // Benutzer erst nach dem ersten Zeichnen bekannt → gemerkte Filter jetzt laden
  useEffect(() => {
    if (!meinSub || filterGeladenFuer.current === meinSub) return
    filterGeladenFuer.current = meinSub
    geradeGeladen.current = true
    const f = filterLaden(meinSub)
    setSicht(f.sicht ?? "alle")
    setStatusFilter(f.status ?? OFFENE_STATI)
    setZugFilter(f.zug ?? null)
    setDatumsstrahlAn(f.datumsstrahl ?? false)
    setStrahlTag(f.tag ?? null)
  }, [meinSub])

  // Jede Filteränderung sofort merken
  useEffect(() => {
    if (!meinSub || filterGeladenFuer.current !== meinSub) return
    if (geradeGeladen.current) {
      // Durchlauf mit den alten Werten direkt nach dem Nachladen auslassen
      geradeGeladen.current = false
      return
    }
    filterSpeichern(meinSub, {
      sicht,
      status: statusFilter,
      zug: zugFilter,
      datumsstrahl: datumsstrahlAn,
      tag: strahlTag,
    })
  }, [meinSub, sicht, statusFilter, zugFilter, datumsstrahlAn, strahlTag])

  // Ein Klick zurück auf den Ausgangszustand: Neu, Gesichtet, In Umsetzung —
  // alle übrigen Filter aus.
  const alleFilterStandard =
    istDefault && sicht === "alle" && zugFilter === null && !datumsstrahlAn && strahlTag === null
  function filterZuruecksetzen() {
    setSicht("alle")
    setStatusFilter(OFFENE_STATI)
    setZugFilter(null)
    setDatumsstrahlAn(false)
    setStrahlTag(null)
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports", scope, statusFilter, zugFilter],
    queryFn: () =>
      api.reports.list({
        scope,
        status: statusFilter,
        ...(zugFilter ? { zug: zugFilter } : {}),
      }),
    enabled: statusFilter.length > 0,
  })

  // EINE Pill-Reihe je Seite (12.08.2026), Inhalt = die GEGENSEITE,
  // bewusst OHNE Status-Filter geladen — kein Filter verdeckt Sprungziele.
  //
  // Melder: Nachrichten der Entwicklung — Ungelesenes (ungelesen=true zählt
  // serverseitig nur die Gegenseite) VEREINT mit offenen Fragen (die bleiben
  // auch nach dem Lesen, bis geantwortet ist).
  const { data: rueckfragenDaten } = useQuery({
    queryKey: ["reports", "rueckfragen", scope],
    queryFn: () => api.reports.list({ scope, rueckfrage: true }),
    enabled: !istReviewer,
    refetchInterval: 60_000,
  })
  const { data: ungeleseneDaten } = useQuery({
    queryKey: ["reports", "ungelesene", scope],
    queryFn: () => api.reports.list({ scope, ungelesen: true }),
    enabled: !istReviewer,
    refetchInterval: 60_000,
  })
  const vonEntwicklung = (() => {
    const alle = [...(ungeleseneDaten?.data ?? []), ...(rueckfragenDaten?.data ?? [])]
    const gesehen = new Set<number>()
    return alle.filter((r) => (gesehen.has(r.id) ? false : (gesehen.add(r.id), true)))
  })()

  // Entwicklung: Anfragen/Antworten der Melder — letzte Nachricht kam vom
  // Kunden, wir sind dran. Deckt auch „gelesen, aber unbeantwortet" ab.
  const { data: antwortenDaten } = useQuery({
    queryKey: ["reports", "antworten", scope],
    queryFn: () => api.reports.list({ scope, antwort: true }),
    enabled: istReviewer,
    refetchInterval: 60_000,
  })
  const antworten = antwortenDaten?.data ?? []

  const liste = data?.data ?? []
  const inSicht = (report: Report) =>
    sicht === "alle"
      ? true
      : sicht === "meine"
        ? report.melderSub === meinSub
        : report.melderSub !== meinSub

  // Angezeigte Reihenfolge (Filter + Sortierung wie die Karten) für
  // „vorige / nächste Meldung" auf der Meldungsseite (Hans 14.10.2026) —
  // sessionStorage: gilt für diesen Tab, überlebt ein Neuladen.
  const angezeigt = liste
    .filter(inSicht)
    .slice()
    .sort((a, b) => Number(b.rueckfrageOffen) - Number(a.rueckfrageOffen))
  const reihenfolge = angezeigt.map((r) => r.id).join(",")
  useEffect(() => {
    if (!data) return
    reihenfolgeMerken(reihenfolge.split(",").filter(Boolean).map(Number))
  }, [data, reihenfolge])

  // Anwendungs-Hinweis nur bei mehreren Anwendungen der Instanz
  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const appName = new Map((anwendungen ?? []).map((a) => [a.id, a.name]))
  const mehrereApps = (anwendungen?.length ?? 0) > 1
  // Kundenname (Hans 14.10.2026): im Kopf der Kunde der Sicht, auf den
  // Karten je Meldung, sobald mehrere Kunden sichtbar sind
  const { kundeVonAnwendung, kundeDerSicht, mehrereKunden } = useKunden()

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="mb-1.5 flex items-center gap-3">
            <h1 className="text-[26px] font-bold tracking-tight">Meldungen</h1>
            <span className="flex items-center gap-1.5 text-[26px] font-bold tracking-tight text-primary">
              <Building2 className="h-5 w-5" />
              {kundeDerSicht ?? (mehrereKunden ? "Alle Kunden" : "")}
            </span>
            <Uhr />
          </div>
          <p className="text-sm text-muted-foreground">
            Alle Fehler und Änderungswünsche, die du oder deine Organisation erfasst habt.
          </p>
        </div>
        <Link
          to="/melden"
          className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" />
          Neue Meldung
        </Link>
      </div>

      {(
        // Kein Entweder-oder mehr: die Liste zeigt immer alles, die Chips
        // filtern nur. Eigene Meldungen tragen den Farbbalken links.
        <div className="mb-5 inline-flex rounded-[10px] bg-secondary p-[3px]">
          {(
            [
              ["alle", "Alle"],
              ["meine", "Meine"],
              ["andere", "Andere"],
            ] as const
          ).map(([wert, label]) => (
            <button
              key={wert}
              type="button"
              onClick={() => setSicht(wert)}
              className={`rounded-lg px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                sicht === wert
                  ? "bg-card text-foreground shadow-card"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Status-Filter: Default = offene; Klick schaltet um, Doppelklick isoliert. */}
      <div className="mb-5 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[13px] font-medium text-muted-foreground">Status:</span>
        {sichtbareStati.map((s) => {
          const aktiv = statusFilter.includes(s)
          return (
            <button
              key={s}
              type="button"
              onClick={() => chipKlick(s)}
              onDoubleClick={() => chipDoppelklick(s)}
              title="Klick: an/aus · Doppelklick: nur diesen Status"
              aria-pressed={aktiv}
              className={`inline-flex select-none items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
                aktiv
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-transparent bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${STATUS_PUNKT[s]} ${aktiv ? "" : "opacity-40"}`} />
              {STATUS_LABEL[s]}
            </button>
          )
        })}
        {/* Wer ist dran (#101): abgeleitet aus Status + Unterhaltung, kein eigener Status */}
        <span className="ml-3 text-[13px] font-medium text-muted-foreground">Wer ist dran:</span>
        {(["dev", "melder"] as const).map((z) => {
          const aktiv = zugFilter === z
          return (
            <button
              key={z}
              type="button"
              onClick={() => setZugFilter((akt) => (akt === z ? null : z))}
              title={z === "dev" ? "Nur Meldungen, bei denen die Entwicklung am Zug ist" : "Nur Meldungen, bei denen der Melder am Zug ist"}
              aria-pressed={aktiv}
              className={`inline-flex select-none items-center rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
                aktiv
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-transparent bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              {ZUG_LABEL[z]}
            </button>
          )
        })}
        {!alleFilterStandard && (
          <button
            type="button"
            onClick={filterZuruecksetzen}
            title="Zurück auf Neu, Gesichtet und In Umsetzung — alle anderen Filter aus"
            className="ml-2 inline-flex items-center gap-1 rounded-full border-2 border-transparent bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Filter zurücksetzen
          </button>
        )}
        <button
          type="button"
          onClick={() => setDatumsstrahlAn((an) => !an)}
          aria-pressed={datumsstrahlAn}
          title="Datumsstrahl ein/aus — Meldungen als Balken auf der Zeitachse"
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
            datumsstrahlAn
              ? "border-primary bg-primary/10 text-foreground"
              : "border-transparent bg-secondary text-muted-foreground hover:text-foreground"
          }`}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          Datumsstrahl
        </button>
      </div>

      {/* EINE Pill-Reihe je Seite (12.08.2026): Farbe = Absenderseite,
          wie die Blasen im Strang. Der Melder sieht BLAU (Nachrichten der
          Entwicklung — Ungelesenes + offene Fragen), die Entwicklung sieht
          GELB (der Melder hat geschrieben, wir sind dran). Immer sichtbar,
          unabhängig von allen Filtern; Klick springt in die Meldung. */}
      {!istReviewer && vonEntwicklung.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3">
          <span className="flex shrink-0 items-center gap-1.5 text-[13px] font-semibold text-primary">
            <MessageCircle className="h-4 w-4" />
            Von der Entwicklung:
          </span>
          {vonEntwicklung.map((r) => (
            <Link
              key={r.id}
              to={`/meldungen/${r.id}`}
              title={
                r.rueckfrageOffen
                  ? `${r.titel || "ohne Titel"} — Frage offen, bitte antworten`
                  : `${r.titel || "ohne Titel"} — ${STATUS_LABEL[r.status]}`
              }
              className="inline-flex max-w-[280px] items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground transition-opacity hover:opacity-85"
            >
              <span className="truncate">{pillText(r)}</span>
              {r.rueckfrageOffen && (
                <span className="rounded-full bg-warning px-1.5 text-[10px] font-bold text-warning-foreground">
                  Frage
                </span>
              )}
            </Link>
          ))}
        </div>
      )}

      {istReviewer && antworten.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-warning/40 bg-warning/5 px-4 py-3">
          <span className="flex shrink-0 items-center gap-1.5 text-[13px] font-semibold text-warning">
            <MessageCircle className="h-4 w-4" />
            Vom Melder — du bist dran:
          </span>
          {antworten.map((r) => (
            <Link
              key={r.id}
              to={`/meldungen/${r.id}`}
              title={`${r.titel || "ohne Titel"} — ${STATUS_LABEL[r.status]}`}
              className="inline-flex max-w-[280px] items-center rounded-full bg-warning px-3 py-1 text-xs font-bold text-warning-foreground transition-opacity hover:opacity-85"
            >
              <span className="truncate">{pillText(r)}</span>
            </Link>
          ))}
        </div>
      )}


      {statusFilter.length === 0 && (
        <div className="rounded-xl border bg-card px-6 py-12 text-center text-muted-foreground">
          Kein Status ausgewählt.{" "}
          <button
            type="button"
            onClick={() => setStatusFilter(OFFENE_STATI)}
            className="text-primary underline"
          >
            Offene Meldungen anzeigen
          </button>
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">Lade Meldungen …</p>}

      {error != null && (
        <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
          {apiError(error)}
        </p>
      )}

      {data && liste.length === 0 && (
        <div className="rounded-xl border bg-card px-6 py-12 text-center text-muted-foreground">
          {istDefault ? (
            <>
              Keine offenen Meldungen.
              <br />
              <Link to="/melden" className="text-sm text-primary underline">
                Neue Meldung erfassen
              </Link>
            </>
          ) : (
            <>
              Keine Meldungen im gewählten Status.
              <br />
              <button
                type="button"
                onClick={() => setStatusFilter(OFFENE_STATI)}
                className="text-sm text-primary underline"
              >
                Zurück zu „nur offene“
              </button>
            </>
          )}
        </div>
      )}

      {data && liste.length > 0 && datumsstrahlAn && (
        <Zeitstrahl reports={liste.filter(inSicht)} tag={strahlTag} onTag={setStrahlTag} />
      )}

      {data && liste.length > 0 && !datumsstrahlAn && (
        <div className="flex flex-col gap-2.5">
          {/* Wartende Antworten zuerst — innerhalb der Gruppen bleibt die
              Server-Sortierung (neueste oben) erhalten (sort ist stabil). */}
          {angezeigt.map((report) => (
              <MeldungsKarte
                key={report.id}
                report={report}
                orgSicht={true}
                appName={
                  [
                    mehrereKunden ? kundeVonAnwendung(report.applicationId) : null,
                    mehrereApps
                      ? appName.get(report.applicationId) ?? String(report.applicationId)
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || null
                }
                eigene={report.melderSub === meinSub}
                issueVerlinken={istReviewer}
              />
            ))}
        </div>
      )}
    </div>
  )
}
